<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import { useLanguageValidationErrors } from '@/composables';
import { provideEditLocale } from '@/composables/data-display-utils';
import { getAccountTypeOptions } from '@/helpers/accountType.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { localizableString } from '@/helpers/validation.helper';
import type { CommunityDto, Locales } from '@/services/types/api.types';
import { useCommunityStore } from '@/stores/community.store';

const props = defineProps<{ community: CommunityDto }>();
defineEmits([...useDialogPluginComponent.emits]);

const { t } = useI18n();
const communityStore = useCommunityStore();
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// The duplicate inherits its languages from the source, so unlike the create
// dialog there is nothing to choose here.
const languageEnabled = ref<Locales[]>(props.community.language_enabled);
const editLocale = provideEditLocale(languageEnabled);

// Prefilled from the source, then renamed by the admin. Copied so editing the
// form does not mutate the row behind the list.
const { handleSubmit, errors, meta, isSubmitting } = useForm({
  initialValues: {
    account_type: undefined as number | undefined,
    title: { ...props.community.title },
    description: { ...props.community.description },
  },
  validationSchema: computed(() =>
    yup.object({
      // Not inherited: a copy of a demo can be a customer's, and the reverse.
      account_type: yup.number().required(),
      title: localizableString(languageEnabled.value),
      description: localizableString(languageEnabled.value),
    }),
  ),
});
const languageErrors = useLanguageValidationErrors(errors);

const onSubmit = handleSubmit(
  async (values) =>
    await wrapSavingOperation(
      () => communityStore.duplicateCommunity(props.community, values),
      () => onDialogOK(),
    ),
);
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('form.dialogs.community.duplicate') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <p class="text-caption text-grey-8">{{ t('community.dialogs.duplicate-hint') }}</p>
        <localized-select
          name="account_type"
          :label="t('customerOverview.accountType.label')"
          :options="getAccountTypeOptions()"
          emit-value
          map-options
          outlined
        />
        <localized-input
          name="title"
          :label="t('community.title')"
          :editLocale="editLocale"
          :language_enabled="languageEnabled"
          outlined
        />
        <localized-input
          name="description"
          :label="t('community.description')"
          :editLocale="editLocale"
          :language_enabled="languageEnabled"
          outlined
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <!-- A duplicate is slow and not idempotent: a second click would create a second community. -->
        <q-btn
          color="primary"
          :label="t('global.save')"
          :disabled="!meta.valid || isSubmitting"
          :loading="isSubmitting"
          @click="onSubmit"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
